import React, {useState} from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  IconButton,
  Divider,
} from '@mui/material';
import {
  isToday,
  isYesterday,
  isLastWeek,
  isLastMonth,
  isOlder,
} from '../../util/date';
import {Pencil, Trash2} from 'lucide-react';
import PropTypes from 'prop-types';
import {RenameConversationForm} from './RenameConversationForm';
import './AgentChat.css';
import {GREY_BORDER} from '../../theme';
export const ConversationHistory = ({
  conversationId,
  conversations,
  loadConversation,
  deleteConversation,
  renameConversation,
  closeMenu,
}) => {
  const [isLoadingConversations, setIsLoadingConversations] = useState(false);
  const [isRenaming, setIsRenaming] = useState(false);
  const [conversationToRename, setConversationToRename] = useState(null);
  const [initialTitle, setInitialTitle] = useState('');

  // Group conversations by time periods
  const groupConversationsByDate = () => {
    return {
      today: conversations.filter((conv) => isToday(conv.updated_at)),
      yesterday: conversations.filter((conv) => isYesterday(conv.updated_at)),
      lastWeek: conversations.filter((conv) => isLastWeek(conv.updated_at)),
      lastMonth: conversations.filter((conv) => isLastMonth(conv.updated_at)),
      older: conversations.filter((conv) => isOlder(conv.updated_at)),
    };
  };

  // Handle opening the rename form
  const handleRenameClick = (id) => {
    const conversation = conversations.find((c) => c.id === id);
    if (conversation) {
      setConversationToRename(id);
      setInitialTitle(conversation.title || '');
      setIsRenaming(true);
    }
  };

  // Handle going back to conversation list
  const handleCancelRename = () => {
    setIsRenaming(false);
    setConversationToRename(null);
  };

  // Handle rename submission
  const handleRenameSubmit = (newTitle) => {
    if (conversationToRename) {
      renameConversation(conversationToRename, newTitle);
      handleCancelRename();
    }
  };

  const renderConversationItem = (conversation) => (
    <ListItem key={conversation.id} disablePadding sx={{display: 'block'}}>
      <ListItemButton
        selected={conversation.id === conversationId}
        onClick={async () => {
          setIsLoadingConversations(true);
          await loadConversation(conversation.id);
          closeMenu();
          setIsLoadingConversations(false);
        }}
        className="conversation-item"
      >
        <ListItemText
          primary={
            <Typography
              variant="body2"
              className={`conversation-title ${conversation.id === conversationId ? 'conversation-title-selected' : 'conversation-title-normal'}`}
              title={conversation.title || 'Untitled Conversation'}
            >
              {conversation.title || 'Untitled Conversation'}
            </Typography>
          }
        />

        <Box className="conversation-actions">
          <IconButton
            edge="end"
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              handleRenameClick(conversation.id);
            }}
          >
            <Pencil size={16} />
          </IconButton>
          <IconButton
            edge="end"
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              deleteConversation(conversation.id);
            }}
          >
            <Trash2 size={16} />
          </IconButton>
        </Box>
      </ListItemButton>
    </ListItem>
  );

  const renderGroupedConversations = () => {
    const grouped = groupConversationsByDate();

    return (
      <>
        {/* Today */}
        {grouped.today.length > 0 && (
          <>
            {grouped.today.map(renderConversationItem)}
            <Divider className="time-group-divider" />
          </>
        )}

        {/* Yesterday */}
        {grouped.yesterday.length > 0 && (
          <>
            <Box className="time-group-header">
              <Typography
                variant="body2"
                color="text.secondary"
                className="time-group-label"
              >
                Yesterday
              </Typography>
            </Box>
            {grouped.yesterday.map(renderConversationItem)}
            <Divider className="time-group-divider" color={GREY_BORDER} />
          </>
        )}

        {/* Last 7 days */}
        {grouped.lastWeek.length > 0 && (
          <>
            <Box className="time-group-header">
              <Typography
                variant="body2"
                color="text.secondary"
                className="time-group-label"
              >
                7 days ago
              </Typography>
            </Box>
            {grouped.lastWeek.map(renderConversationItem)}
            <Divider className="time-group-divider" />
          </>
        )}

        {/* Last month */}
        {grouped.lastMonth.length > 0 && (
          <>
            <Box className="time-group-header">
              <Typography
                variant="body2"
                color="text.secondary"
                className="time-group-label"
              >
                1 month ago
              </Typography>
            </Box>
            {grouped.lastMonth.map(renderConversationItem)}
            <Divider className="time-group-divider" />
          </>
        )}

        {/* Older */}
        {grouped.older.length > 0 && (
          <>
            <Box className="time-group-header">
              <Typography
                variant="body2"
                color="text.secondary"
                className="time-group-label"
              >
                Older
              </Typography>
            </Box>
            {grouped.older.map(renderConversationItem)}
          </>
        )}
      </>
    );
  };

  return (
    <Box className="conversation-container">
      {isRenaming ? (
        <RenameConversationForm
          initialTitle={initialTitle}
          onRename={handleRenameSubmit}
          onCancel={handleCancelRename}
        />
      ) : isLoadingConversations ? (
        <Box className="loading-container">
          <CircularProgress size={24} />
        </Box>
      ) : conversations.length === 0 ? (
        <Box className="empty-container">
          <Typography variant="body2" color="text.secondary">
            No conversations yet
          </Typography>
        </Box>
      ) : (
        <List
          component="nav"
          aria-label="conversations"
          className="conversation-list"
        >
          {renderGroupedConversations()}
        </List>
      )}
    </Box>
  );
};

ConversationHistory.propTypes = {
  conversationId: PropTypes.string,
  conversations: PropTypes.array.isRequired,
  loadConversation: PropTypes.func.isRequired,
  deleteConversation: PropTypes.func.isRequired,
  renameConversation: PropTypes.func.isRequired,
  closeMenu: PropTypes.func.isRequired,
};
